import { make } from "vuex-pathify";
import axios from "axios";
import { differenceInDays, startOfYear, format, parseISO, addDays } from "date-fns";
import { PRODUCTION_FACILITY_TYPES } from "@/util/globals";
import { ORGANIZATION_TYPES } from "@/util/globals";
import { STATUS } from "@/util/globals";
import { sortByNameRuAscending } from "@/util/helpers";

const state = {
    organization: {
        all: {
            id: 0,
            statusId: 3,
            nameRu: 'Все организации',
            bin: '----0000----'
        },
        path: {
            base: `api/Reference/Organization/${ORGANIZATION_TYPES.OilCompany}/`,
            sub: `/${STATUS.Active}`
        }
    },
    showError: false,
    errorMessages: {
        organization: "Error while loading organizations list",
        report: "Error while loading report.",
        export: "An empty table could not be exported."
    },
    errorMessage: "",
    loading: true,
    currentTab: null,
    range: {
        start: {
            label: "Начальная дата",
            value: null
        },
        end: {
            label: "Конечная дата",
            value: null
        }
    },
    reportKpi: {
        numberOfDays: {
            value: 0,
            text: "дне-(й/я)",
            icon: "mdi-calendar-range"
        },
        oilProduction: { value: 0, text: "тонн", icon: "mdi-trending-up" }
    },
    series: [{ name: "", data: [] }],
    chartOptions: {
        //colors: ['#FFFFFF'],// Цвет линий графика
        chart: {
            defaultLocale: 'ru',
            locales: [{
                name: 'ru',
                options: {
                    months: ["Январь",
                        "Февраль",
                        "Март",
                        "Апрель",
                        "Май",
                        "Июнь",
                        "Июль",
                        "Август",
                        "Сентябрь",
                        "Октябрь",
                        "Ноябрь",
                        "Декабрь"],
                    shortMonths: ["Янв",
                        "Фев",
                        "Мар",
                        "Апр",
                        "Май",
                        "Июн",
                        "Июл",
                        "Авг",
                        "Сен",
                        "Окт",
                        "Ноя",
                        "Дек"],
                    days: ["Воскресенье",
                        "Понедельник",
                        "Вторник",
                        "Среда",
                        "Четверг",
                        "Пятница",
                        "Суббота"],
                    shortDays: ["Вс",
                        "Пн",
                        "Вт",
                        "Ср",
                        "Чт",
                        "Пт",
                        "Сб"],
                    toolbar: {
                        "download": "Сохранить",
                        "exportToSVG": "Сохранить SVG",
                        "exportToPNG": "Сохранить PNG",
                        "exportToCSV": "Сохранить CSV",
                        "menu": "Меню",
                        "selection": "Выбор",
                        "selectionZoom": "Выбор с увеличением",
                        "zoomIn": "Увеличить",
                        "zoomOut": "Уменьшить",
                        "pan": "Перемещение",
                        "reset": "Сбросить увеличение"
                    }
                }
            }],
            type: "area",
            stacked: false,
            height: 350,
            zoom: {
                type: "x",
                enabled: true,
                autoScaleYaxis: true
            },
            toolbar: {
                autoSelected: "zoom",
            }
        },
        dataLabels: {
            enabled: false,
        },
        markers: {
            size: 0
        },
        title: {
            text: "ISUN Report Produce Chart - organization",
            align: "left",
            style: {
                color: '#FFFFFF',
            },
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.5,
                opacityTo: 0,
                stops: [0, 90, 100]
            }
        },
        yaxis: {
            labels: {
                formatter: function (val) {
                    return val.toFixed(0);
                    // return (val / 1000000).toFixed(0);
                },
                style: {
                    colors: '#78909C',
                    // colors: '#FFFFFF',
                }
            },
            title: {
                text: "Нефть (тонн)",
                style: {
                    color: "#78909C",
                }
            }
        },
        xaxis: {
            type: "datetime",
            labels: {
                // datetimeUTC: false,
                style: {
                    colors: '#78909C',
                }

            }
        },
        tooltip: {
            shared: false,
            y: {
                formatter: function (val, opts) {
                    // return (val / 1000000).toFixed(0);
                    const yAxisTitle = opts && opts.w && Array.isArray(opts.w.config.yaxis) && opts.w.config.yaxis[0] && opts.w.config.yaxis[0].title
                        ? String(opts.w.config.yaxis[0].title.text || "")
                        : "";
                    const unit = yAxisTitle.toLowerCase().includes("газ") || yAxisTitle.includes("м³") || yAxisTitle.includes("м3")
                        ? " м³"
                        : " т.";
                    return `${val.toFixed(0).toLocaleString()}${unit}`;
                }
            }
        }
    },
    optionsProduce: {
        title: "Отчет по нефтедобывающим организациям",
        facilityType: PRODUCTION_FACILITY_TYPES.OilProducingCompany,
        path: {
            base: "api/Report/Produce/"
        },
        tabs: [
            {
                id: 1,
                name: "Поставка на транспортные организации",
                link: "Transport",
                disabled: false
            },
            // Скрыто времено по распоряжению Смагулова Аскара 07.04.2022
            // {
            //     id: 2,
            //     name: "Сдача на перерабатывающие заводы",
            //     link: "Process",
            // },
            {
                id: 3,
                name: "Отгрузка на транспортные организации",
                link: "Flow",
                disabled: false
            },
            {
                id: 4,
                name: "Хранение",
                link: "Storage",
                disabled: false
            },
            {
                id: 5,
                name: "Подготовка",
                link: "Preparation",
                disabled: false
            },
            {
                id: 6,
                name: "Отгрузка на экспорт",
                link: "Export",
                disabled: false
            },
        ],

        // Заголовки по умолчанию, показываются при старте страницы
        headers: [
            { text: "Организация", value: "organizationName" },
            // { text: "БИН", value: "bin" },
            { text: "Магистральный нефтепровод", value: "pipeline" },
            // { text: "Местоположение объекта", value: "location" },
            { text: "Прибор учета", value: "meteringDevice" },
            { text: "Дата", value: "date" },
            { text: "Масса", value: "mass" }
        ],
        headersReport: {
            base: [
                { text: "Организация", value: "organizationName" },
                // { text: "БИН", value: "bin" },
                { text: "Магистральный нефтепровод", value: "pipeline" },
                // { text: "Местоположение объекта", value: "location" },
                { text: "Прибор учета", value: "meteringDevice" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" }
            ],
            export: [
                { text: "Организация", value: "organizationName" },
                // { text: "БИН", value: "bin" },
                { text: "Магистральный нефтепровод", value: "pipelineName" },
                // { text: "Местоположение объекта", value: "location" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" }
            ],
            oilFlow: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Магистральный нефтепровод", value: "pipelineName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Масса на начало", value: "massFlowBegin" },
                { text: "Масса на конец", value: "massFlowEnd" },
                { text: "Температура", value: "temperature" },
                { text: "Плотность", value: "density" },
                { text: "Объем", value: "volume" }
            ],
            oilTank: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Плотность", value: "density" },
                { text: "Уровень", value: "tankLevel" },
                { text: "Температура", value: "temperature" },
                { text: "Объем", value: "volume" }
            ]
        },

        organizations: [],
        data: []
    },
    optionsTransport: {
        title: "Отчет по нефтетранспортным организациям",
        facilityType: PRODUCTION_FACILITY_TYPES.OilTransportationCompany,
        path: {
            base: "api/Report/Transport/"
        },
        tabs: [
            {
                id: 1,
                name: "Прием от нефтедобывающих организации",
                link: "1",
                disabled: false
                // link: "DeliveryFromOilProduction",
            },
            {
                id: 2,
                name: "Сдача на перерабатывающие заводы",
                link: "2",
                disabled: false
                // link: "ShipmentToOilProduction",
            },
            {
                id: 3,
                name: "Сдача на транспортные организации",
                link: "3",
                disabled: false
                // link: "ShipmentToTransportCompany",
            },
            {
                id: 4,
                name: "Экспорт",
                link: "4",
                disabled: false
                // link: "ShipmentToExport",
            }
            //{
            //    id: 5,
            //    name: "Хранение (В разработке)",
            //    link: "5",
            //    disabled: true
            //}
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            // { text: "БИН", value: "bin" },
            { text: "Магистральный нефтепровод", value: "pipeline" },
            // { text: "Местоположение объекта", value: "location" },
            { text: "Прибор учета", value: "meteringDevice" },
            { text: "Дата", value: "date" },
            { text: "Масса", value: "mass" }
        ],
        organizations: [],
        data: []
    },
    optionsTerminals: {
        title: "Отчет по нефтяным терминалам",
        facilityType: PRODUCTION_FACILITY_TYPES.OilTerminal,
        path: {
            base: "api/Report/Terminals/"
        },
        tabs: [
            {
                id: 1,
                name: "Прием от добывающих организаций",
                link: "Flow",
                disabled: false
            },
            {
                id: 2,
                name: "Подготовка",
                link: "Preparation",
                disabled: false
            },
            {
                id: 3,
                name: "Хранение",
                link: "Storage",
                disabled: false
            },
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            { text: "Прибор учета", value: "productionFacilityName" },
            { text: "Магистральный нефтепровод", value: "pipelineName" },
            { text: "Дата", value: "date" },
            { text: "Масса", value: "mass" },
            { text: "Масса на начало", value: "massFlowBegin" },
            { text: "Масса на конец", value: "massFlowEnd" },
            { text: "Температура", value: "temperature" },
            { text: "Плотность", value: "density" },
            { text: "Объем", value: "volume" }
        ],
        headersReport: {
            base: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Магистральный нефтепровод", value: "pipelineName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Масса на начало", value: "massFlowBegin" },
                { text: "Масса на конец", value: "massFlowEnd" },
                { text: "Температура", value: "temperature" },
                { text: "Плотность", value: "density" },
                { text: "Объем", value: "volume" }
            ],
            oilTank: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Плотность", value: "density" },
                { text: "Уровень", value: "tankLevel" },
                { text: "Температура", value: "temperature" },
                { text: "Объем", value: "volume" }
            ]
        },
        organizations: [],
        data: []
    },
    optionsRefinery: {
        title: "Отчет по нефтеперерабатывающим организациям",
        facilityType: PRODUCTION_FACILITY_TYPES.OilRefiningCompany,
        path: {
            base: "api/Report/Refinery/"
        },
        tabs: [
            {
                id: 1,
                name: "Прием от нефтетранспортных организаций",
                link: "Transport",
                disabled: false
            },
            {
                id: 2,
                name: "Прием от нефтедобывающих организаций",
                link: "Flow",
                disabled: false
            },
            {
                id: 3,
                name: "Хранение",
                link: "Storage",
                disabled: false
            }
        ],

        // Заголовки по умолчанию, показываются при старте страницы
        headers: [
            { text: "Организация", value: "organizationName" },
            // { text: "БИН", value: "bin" },
            { text: "Магистральный нефтепровод", value: "pipeline" },
            // { text: "Местоположение объекта", value: "location" },
            { text: "Прибор учета", value: "meteringDevice" },
            { text: "Дата", value: "date" },
            { text: "Масса", value: "mass" }
        ],
        headersReport: {
            base: [
                { text: "Организация", value: "organizationName" },
                // { text: "БИН", value: "bin" },
                { text: "Магистральный нефтепровод", value: "pipeline" },
                // { text: "Местоположение объекта", value: "location" },
                { text: "Прибор учета", value: "meteringDevice" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" }
            ],
            oilFlow: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Магистральный нефтепровод", value: "pipelineName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Масса на начало", value: "massFlowBegin" },
                { text: "Масса на конец", value: "massFlowEnd" },
                { text: "Температура", value: "temperature" },
                { text: "Плотность", value: "density" },
                { text: "Объем", value: "volume" }
            ],
            oilTank: [
                { text: "Организация", value: "organizationName" },
                { text: "Прибор учета", value: "productionFacilityName" },
                { text: "Дата", value: "date" },
                { text: "Масса", value: "mass" },
                { text: "Плотность", value: "density" },
                { text: "Уровень", value: "tankLevel" },
                { text: "Температура", value: "temperature" },
                { text: "Объем", value: "volume" }
            ]
        },
        organizations: [],
        data: []
    },
    optionsFactories: {
        title: "Отчет по битумным заводам",
        facilityType: PRODUCTION_FACILITY_TYPES.BitumenFactory,
        path: {
            base: "api/Report/Factories"
        },
        tabs: [
            {
                id: 1,
                name: "Прием от нефтетранспортных организаций",
                link: "",
                disabled: false
            }
            //{
            //    id: 2,
            //    name: "Прием от нефтедобывающих заводов (В разработке)",
            //    link: "",
            //    disabled: true
            //},
            //{
            //    id: 3,
            //    name: "Хранение (В разработке)",
            //    link: "",
            //    disabled: true
            //},
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            // { text: "БИН", value: "bin" },
            { text: "Магистральный нефтепровод", value: "pipeline" },
            // { text: "Местоположение объекта", value: "location" },
            { text: "Прибор учета", value: "meteringDevice" },
            { text: "Дата", value: "date" },
            { text: "Масса", value: "mass" }
        ],
        organizations: [],
        data: []
    },
    
    optionsUsersActivity: {
        title: "Отчет по активности пользователей",
        path: {
            base: "api/Account/Activity"
        },
        headers: [
            // { text: "UserId", value: "userId" },
            { text: "Логин", value: "userName" },
            { text: "Роль", value: "role" },
            // { text: "PersonId", value: "personId" },
            { text: "Имя", value: "firstName" },
            { text: "Фамилия", value: "lastName" },
            { text: "Действие", value: "methodName" },
            // { text: "Задача", value: "task" },
            // { text: "Результат", value: "result" },
            { text: "Примечание", value: "description" }
        ],
        data: []
    },
    optionsDataTransferAudit: {
        title: "Отчет по сбоям передачи данных",
        facilityType: PRODUCTION_FACILITY_TYPES.OilTransportationCompany,
        path: {
            base: "api/Analytic/Transfers/OilDatas"
        },
        tabs: [
            {
                id: 1,
                name: "OilDatas",
                link: "DeliveryFromOilProduction"
            },
            {
                id: 2,
                name: "OilFlowDatas",
                link: "ShipmentToOilProduction"
            },
            {
                id: 3,
                name: "OilTanks",
                link: "ShipmentToTransportCompany"
            }
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            { text: "Прибор учета", value: "productionFacilityName" },
            { text: "Дата начала инцидента", value: "begin" },
            { text: "Дата окончания инцидента", value: "end" },
            { text: "Источник", value: "source" },
            { text: "Статус инцидента", value: "status" }
        ],
        organizations: [],
        data: [],
        dataFake: [
            {
                orgName: "КазТрансНефть",
                begin: "01.10.2021",
                end: "02.10.2021",
                status: true
            },
            {
                orgName: "КаспийНефть",
                begin: "01.10.2021",
                end: "",
                status: false
            }
        ]
    },
    optionsAnomalies: {
        title: "Аномальные значения",
        path: {
            base: "api/Analytic/Limits",
            max: "/Max",
            min: "/Min"
        },
        tabs: [
            {
                id: 0,
                name: "Максимальные",
                link: "Max"
            },
            {
                id: 1,
                name: "Минимальные",
                link: "Min"
            }
        ],
        headers: [
            // { text: "Id", value: "id" },
            { text: "Организациия", value: "orgName" },
            { text: "Прибор учета", value: "meteringDevice" },
            // { text: "Код", value: "productionFacilityCode" },
            // { text: "БИН", value: "bin" },
            // { text: "Нефтепровод", value: "pipeline" },
            { text: "Дата инцидента", value: "period" },
            { text: "Значение", value: "mass" },
            // { text: "Регион", value: "location" }
        ],
        items: [],
        data: {
            max: [],
            min: []
        }
    },
    optionsKPI: {
        title: "Плановые показатели",
        path: {
            base: "api/Analytic/Limits",
        },
        tabs: [
            {
                id: 0,
                name: "Добыча",
                link: "produce"
            },
            {
                id: 1,
                name: "Переработка",
                link: "refinery"
            },
            {
                id: 2,
                name: "Экспорт",
                link: "export"
            }
        ],
        headers: [
            { text: "Месяц", value: "month" },
            { text: "План (т. т)", value: "plan" },
            { text: "Факт (т. т)", value: "fact" },
            { text: "Исполнение (%)", value: "percent" },
            { text: "Отклонение (%)", value: "deviation" },
            { text: "Отклонение (т. т.)", value: "weightDeviation" },
        ],
        items: [],
        data: [],
        organizations: {},
    },
    optionsGas: {
        title: "Добыча газа",
        path: {
            base: "api/Report/Gas"
        },
        tabs: [
            {
                id: 1,
                name: "Подготовка сырого газа",
                operationTypeId: [17, 8, 12],
                disabled: false
            },
            {
                id: 2,
                name: "Факельная установка",
                operationTypeId: 19,
                disabled: false
            },
            {
                id: 3,
                name: "Собственные тех. нужды",
                operationTypeId: 20,
                disabled: false
            },
            {
                id: 4,
                name: "Обратная закачка газа",
                operationTypeId: 21,
                disabled: false
            },
            {
                id: 5,
                name: "Подземные хранилища газа",
                operationTypeId: 18,
                disabled: false
            }
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            { text: "Объект", value: "objectName" },
            { text: "Отчетная дата", value: "dateTime" },
            { text: "Дата регистрации", value: "regDateTime" },
            { text: "Температура", value: "temperature" },
            { text: "Объем", value: "volume" },
            { text: "Давление", value: "pressure" },
            { text: "Тип данных", value: "isDaily" }
        ],
        organizations: [],
        data: [],
        allCount: 0
    },
    optionsGasTransport: {
        title: "Транспортировка газа",
        path: {
            base: "api/Report/Gas"
        },
        tabs: [
            {
                id: 1,
                name: "Сдача на газоперерабатывающие заводы",
                operationTypeId: 13,
                disabled: false
            },
            {
                id: 2,
                name: "Сдача в газотранспортные организации",
                operationTypeId: 14,
                disabled: false
            },
            {
                id: 3,
                name: "Сдача на экспорт",
                operationTypeId: 15,
                disabled: false
            }
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            { text: "Объект", value: "objectName" },
            { text: "Отчетная дата", value: "dateTime" },
            { text: "Дата регистрации", value: "regDateTime" },
            { text: "Температура", value: "temperature" },
            { text: "Объем", value: "volume" },
            { text: "Давление", value: "pressure" },
            { text: "Тип данных", value: "isDaily" }
        ],
        organizations: [],
        data: [],
        allCount: 0
    },
    optionsGasRefinery: {
        title: "Переработка газа",
        path: {
            base: "api/Report/Gas"
        },
        tabs: [
            {
                id: 1,
                name: "Товарный газ",
                operationTypeId: 16,
                disabled: false
            }
        ],
        headers: [
            { text: "Организация", value: "organizationName" },
            { text: "Объект", value: "objectName" },
            { text: "Отчетная дата", value: "dateTime" },
            { text: "Дата регистрации", value: "regDateTime" },
            { text: "Температура", value: "temperature" },
            { text: "Объем", value: "volume" },
            { text: "Давление", value: "pressure" },
            { text: "Тип данных", value: "isDaily" }
        ],
        organizations: [],
        data: [],
        allCount: 0
    }
};

const mutations = {
    ...make.mutations(state),
    //*** ***//
    CHART_SERIES: (state, seriesItem) => {
        state.series = [seriesItem];
    },
    // https://bestofvue.com/repo/apexcharts-vue-apexcharts-vuejs-charts
    CHART_OPTIONS_TITLE: (state, chartTitle) => {
        state.chartOptions = {
            ...state.chartOptions,
            ...{
                title: {
                    text: chartTitle,
                    align: "left",
                    style: {
                        color: '#FFFFFF',
                    },
                }
            }
        };
    },
    CHART_YAXIS_TITLE: (state, text) => {
        if (!state.chartOptions.yaxis) state.chartOptions.yaxis = {};
        if (!state.chartOptions.yaxis.title) state.chartOptions.yaxis.title = {};
        state.chartOptions.yaxis.title.text = text;
    },
    KPI_ORGANIZATIONS: (state, data) => {
        state.optionsKPI.organizations = data;
    },
    SET_KPI_DATA: (state, data) => {
        state.optionsKPI.data[data.type] = data.plan;
    },
    PRODUCE_ORGANIZATIONS: (state, data) => {
        state.optionsProduce.organizations = data;
    },
    PRODUCE_DATA: (state, data) => {
        state.optionsProduce.data = data;
    },
    SET_PRODUCE_HEADERS: (state, type) => {
        state.optionsProduce.headers =
            type == "flow"
                ? state.optionsProduce.headersReport.oilFlow
                : type == "storage" || type == "preparation"
                    ? state.optionsProduce.headersReport.oilTank
                        : type == "export"
                            ? state.optionsProduce.headersReport.export
                    : state.optionsProduce.headersReport.base;
    },
    TRANSPORT_ORGANIZATIONS: (state, data) => {
        state.optionsTransport.organizations = data;
    },
    TRANSPORT_DATA: (state, data) => {
        state.optionsTransport.data = data;
    },
    SET_REFINERY_HEADERS: (state, type) => {
        state.optionsRefinery.headers =
            type == "transport"
                ? state.optionsRefinery.headersReport.base
                : type == "flow"
                    ? state.optionsRefinery.headersReport.oilFlow
                    : state.optionsRefinery.headersReport.oilTank;
    },
    SET_TERMINALS_HEADERS: (state, type) => {
        state.optionsTerminals.headers =
            type == "flow"
                ? state.optionsTerminals.headersReport.base
                : type == "storage"
                    ? state.optionsTerminals.headersReport.oilTank
                    : state.optionsTerminals.headersReport.oilTank;
    },
    REFINERY_ORGANIZATIONS: (state, data) => {
        state.optionsRefinery.organizations = data;
    },
    REFINERY_DATA: (state, data) => {
        state.optionsRefinery.data = data;
    },
    TERMINALS_ORGANIZATIONS: (state, data) => {
        state.optionsTerminals.organizations = data;
    },
    TERMINALS_DATA: (state, data) => {
        state.optionsTerminals.data = data;
    },
    FACTORIES_ORGANIZATIONS: (state, data) => {
        state.optionsFactories.organizations = data;
    },
    FACTORIES_DATA: (state, data) => {
        state.optionsFactories.data = data;
    },
    GAS_ORGANIZATIONS: (state, data) => {
        state.optionsGas.organizations = data;
    },
    GAS_DATA: (state, data) => {
        state.optionsGas.data = data;
    },
    GAS_ALL_COUNT: (state, count) => {
        state.optionsGas.allCount = count;
    },
    GAS_TRANSPORT_ORGANIZATIONS: (state, data) => {
        state.optionsGasTransport.organizations = data;
    },
    GAS_TRANSPORT_DATA: (state, data) => {
        state.optionsGasTransport.data = data;
    },
    GAS_TRANSPORT_ALL_COUNT: (state, count) => {
        state.optionsGasTransport.allCount = count;
    },
    GAS_REFINERY_ORGANIZATIONS: (state, data) => {
        state.optionsGasRefinery.organizations = data;
    },
    GAS_REFINERY_DATA: (state, data) => {
        state.optionsGasRefinery.data = data;
    },
    GAS_REFINERY_ALL_COUNT: (state, count) => {
        state.optionsGasRefinery.allCount = count;
    },
    ANOMALIES_MAX_DATA: (state, data) => {
        state.optionsAnomalies.data.max = data;
    },
    ANOMALIES_MIN_DATA: (state, data) => {
        state.optionsAnomalies.data.min = data;
    },
    ANOMALIES_ITEM_SELECT: (state, index) => {
        state.optionsAnomalies.items = state.optionsAnomalies.data[index];
    },
    TRANSFER_AUDIT_ORGANIZATIONS: (state, data) => {
        state.optionsDataTransferAudit.organizations = data;
    },
    TRANSFER_AUDIT_DATA: (state, data) => {
        state.optionsDataTransferAudit.data = data;
    },
    USERS_ACTIVITY_DATA: (state, data) => {
        state.optionsUsersActivity.data = data;
    },
    LOADING: (state, isLoading) => {
        state.loading = isLoading;
    },
    KPI: (state, payload) => {
        state.reportKpi.numberOfDays.value = payload.days;
        state.reportKpi.oilProduction.value = payload.values;
        const yAxisTitle = state.chartOptions && state.chartOptions.yaxis && state.chartOptions.yaxis.title
            ? String(state.chartOptions.yaxis.title.text || "")
            : "";
        state.reportKpi.oilProduction.text = yAxisTitle.toLowerCase().includes("газ") || yAxisTitle.includes("м³") || yAxisTitle.includes("м3")
            ? "м³"
            : "тонн";
    },
    RANGE: (state, data) => {
        state.range.start.value = data.start;
        state.range.end.value = data.end;
    },
    SET_RANGE_YTD: (state) => {
        state.range.start.value = format(startOfYear(Date.now()), "yyyy-MM-dd");
        state.range.end.value = format(Date.now(), "yyyy-MM-dd");
    },
    CURRENT_TAB: (state, data) => {
        state.currentTab = data;
    },
    ERROR: (state, isError) => {
        state.showError = isError;
    },
    ERROR_MESSAGE: (state, message) => {
        state.errorMessage = message;
    }
};

const actions = {
    ...make.actions(state),
    setKpi: ({ commit }, payload) => {
        commit("KPI", payload);
    },
    resetProduceReport: ({ commit }, tab) => {
        commit("SET_PRODUCE_HEADERS", tab.link.toLowerCase());
        commit("PRODUCE_DATA", []);
        commit("KPI", { days: 0, values: 0 });
        commit("SET_RANGE_YTD");
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
    },
    fetchProduceOrganizations: async ({ commit }, type) => {
        try {
            const path = `api/Report/Produce/Organization/${type}`;
            const response = await axios.get(path);
            const organizations = normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all)
            commit("PRODUCE_ORGANIZATIONS", organizations);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchProduceData: async ({ commit }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsProduce.path.base + options.path;
            const response = await axios.get(path, { params: options.params });
            const data = normalizeOilReportItems(response);
            if (data.length > 0) {
                const reduceDates = convertDataChart(data)
                const chartTitle = `График по всем нефтедобывающим организациям за период ${format(new Date(options.params.date_from), "dd-MM-yyyy")} - ${format(new Date(options.params.date_to), "dd-MM-yyyy")}`;
                const series = {
                    name: chartTitle,
                    data: reduceDates
                };
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", series);
                // console.log(reduceDates)
            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
            commit("PRODUCE_DATA", data);
            const days = differenceInDays(
                Date.parse(options.params.date_to),
                Date.parse(options.params.date_from)
            );
            const values = data.reduce(
                (accumulator, currentValue) =>
                    accumulator + parseInt(currentValue.mass, 10),
                0
            );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    resetTransportReport: ({ commit }, tab) => {
        commit("TRANSPORT_DATA", []);
        commit("KPI", { days: 0, values: 0 });
        commit("SET_RANGE_YTD");
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
    },
    fetchTransportOrganizations: async ({ commit, state }, type) => {
        try {
            const path = `api/Report/Transport/Organization/${type}`;
            const response = await axios.get(path);
            const organizations = normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all)
            commit("TRANSPORT_ORGANIZATIONS", organizations);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchTerminalsOrganizations: async ({ commit, state }) => {
        try {
            const path = `api/Report/Terminals/Organization/`;
            const response = await axios.get(path);
            const organizations = normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all)
            commit("TERMINALS_ORGANIZATIONS", organizations);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchTransportData: async ({ commit }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsTransport.path.base + options.path;
            const response = await axios.get(path, { params: options.params });
            const data = normalizeOilReportItems(response);
            if (data.length > 0) {
                const reduceDates = convertDataChart(data)
                const chartTitle = `График по всем нефтетранспортным организациям за период ${format(new Date(options.params.date_from), "dd-MM-yyyy")} - ${format(new Date(options.params.date_to), "dd-MM-yyyy")}`;
                const series = {
                    name: chartTitle,
                    data: reduceDates
                };
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", series);
            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
            commit("TRANSPORT_DATA", data);
            const days = differenceInDays(
                Date.parse(options.params.date_to),
                Date.parse(options.params.date_from)
            );
            const values = data.reduce(
                (accumulator, currentValue) =>
                    accumulator + parseInt(currentValue.mass, 10),
                0
            );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    resetRefineryReport: ({ commit }, tab) => {
        commit("SET_REFINERY_HEADERS", tab.link.toLowerCase());
        commit("REFINERY_DATA", []);
        commit("KPI", { days: 0, values: 0 });
        commit("SET_RANGE_YTD");
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
    },
    resetTerminalsReport: ({ commit }, tab) => {
        commit("SET_TERMINALS_HEADERS", tab.link.toLowerCase());
        commit("TERMINALS_DATA", []);
        commit("KPI", { days: 0, values: 0 });
        commit("SET_RANGE_YTD");
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
    },
    fetchRefineryOrganizations: async ({ commit, state }, type) => {
        try {
            const path = `api/Report/Refinery/Organization/${type}`;
            const response = await axios.get(path);
            const organizations = normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all)
            commit("REFINERY_ORGANIZATIONS", organizations);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },

    fetchRefineryData: async ({ commit }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsRefinery.path.base + options.path;
            const response = await axios.get(path, { params: options.params });
            const data = normalizeOilReportItems(response);
            if (data.length > 0) {
                const reduceDates = convertDataChart(data)
                const chartTitle = `График по всем нефтеперерабатывающим организациям за период ${format(new Date(options.params.date_from), "dd-MM-yyyy")} - ${format(new Date(options.params.date_to), "dd-MM-yyyy")}`;
                const series = {
                    name: chartTitle,
                    data: reduceDates
                };
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", series);
            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
            commit("REFINERY_DATA", data);
            const days = differenceInDays(
                Date.parse(options.params.date_to),
                Date.parse(options.params.date_from)
            );
            const values = data.reduce(
                (accumulator, currentValue) =>
                    accumulator + parseInt(currentValue.mass, 10),
                0
            );
            commit("KPI", { days, values });
            commit("LOADING", false);
            return response;
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchTerminalsData: async ({ commit }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsTerminals.path.base + options.path;
            const response = await axios.get(path, { params: options.params });
            const data = normalizeOilReportItems(response);
            if (data.length > 0) {
                const reduceDates = convertDataChart(data)
                const chartTitle = `График по всем нефтяным терминалам за период ${format(new Date(options.params.date_from), "dd-MM-yyyy")} - ${format(new Date(options.params.date_to), "dd-MM-yyyy")}`;
                const series = {
                    name: chartTitle,
                    data: reduceDates
                };
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", series);

            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
            commit("TERMINALS_DATA", data);
            const days = differenceInDays(
                Date.parse(options.params.date_to),
                Date.parse(options.params.date_from)
            );
            const values = data.reduce(
                (accumulator, currentValue) =>
                    accumulator + parseInt(currentValue.mass, 10),
                0
            );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    resetFactoriesReport: ({ commit }) => {
        commit("FACTORIES_DATA", []);
        commit("KPI", { days: 0, values: 0 });
        commit("SET_RANGE_YTD");
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
    },

    fetchFactoriesOrganizations: async ({ commit, state }) => {
        try {
            //const path = state.organization.path.base + state.optionsFactories.facilityType + state.organization.path.sub;
            const path = `api/Report/Factories/Organization`;
            const response = await axios.get(path);
            commit("FACTORIES_ORGANIZATIONS", normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending));
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },

    fetchFactoriesData: async ({ commit }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsFactories.path.base + options.path;
            const response = await axios.get(path, { params: options.params });
            const data = normalizeOilReportItems(response);
            if (data.length > 0) {
                const reduceDates = convertDataChart(data)
                const chartTitle = `График по всем битумным заводам за период ${format(new Date(options.params.date_from), "dd-MM-yyyy")} - ${format(new Date(options.params.date_to), "dd-MM-yyyy")}`;
                const series = {
                    name: chartTitle,
                    data: reduceDates
                };
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", series);
            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
            commit("FACTORIES_DATA", data);
            const days = differenceInDays(
                Date.parse(options.params.date_to),
                Date.parse(options.params.date_from)
            );
            const values = data.reduce(
                (accumulator, currentValue) =>
                    accumulator + parseInt(currentValue.mass, 10),
                0
            );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },

    resetGasReport: ({ commit }) => {
        commit("GAS_DATA", []);
        commit("GAS_ALL_COUNT", 0);
        commit("KPI", { days: 0, values: 0 });
        commit("RANGE", { start: null, end: null });
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
        commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
    },
    fetchGasOrganizations: async ({ commit, state }) => {
        try {
            const path = `${state.optionsGas.path.base}/Organization`;
            const response = await axios.get(path);
            const organizations = normalizeOrganizationsFromResponse(response).sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all);
            commit("GAS_ORGANIZATIONS", organizations);
            commit("ERROR", false);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchGasData: async ({ commit, state }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsGas.path.base;
            const selectedOperationTypeId = options.params.operationTypeId;
            const operationTypeIds = Array.isArray(selectedOperationTypeId)
                ? selectedOperationTypeId.filter(id => id != null)
                : (selectedOperationTypeId != null ? [selectedOperationTypeId] : []);

            if (operationTypeIds.length > 1) {
                const mergedRequestBase = {
                    organizationId: options.params.organizationId,
                    date_from: options.params.date_from,
                    date_to: options.params.date_to,
                    page: 1,
                    pageSize: 100000
                };
                if (options.params.isDailyData != null) {
                    mergedRequestBase.isDailyData = options.params.isDailyData;
                }
                const responses = await Promise.all(
                    operationTypeIds.map(operationTypeId => axios.get(path, {
                        params: {
                            ...mergedRequestBase,
                            operationTypeId
                        }
                    }))
                );
                const merged = responses
                    .flatMap(response => normalizeGasItemsFromResult(response))
                    .sort((a, b) => new Date(b.regDateTime) - new Date(a.regDateTime));

                const page = options.params.page != null ? options.params.page : 1;
                const pageSize = options.params.pageSize != null ? options.params.pageSize : 10;
                const pageStart = (page - 1) * pageSize;
                const pageEnd = pageStart + pageSize;
                const pagedData = merged.slice(pageStart, pageEnd);

                commit("ERROR", false);
                commit("ERROR_MESSAGE", "");
                commit("GAS_ALL_COUNT", merged.length);
                commit("GAS_DATA", pagedData);

                const chartData = gasReportToChartData(merged, options.params.date_from, options.params.date_to);
                const chartTitle = options.params.date_from && options.params.date_to
                    ? `График по газу за период ${format(new Date(options.params.date_from), "dd.MM.yyyy")} - ${format(new Date(options.params.date_to), "dd.MM.yyyy")}`
                    : "";
                commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", { name: chartTitle, data: chartData });

                const days = options.params.date_from && options.params.date_to
                    ? differenceInDays(Date.parse(options.params.date_to), Date.parse(options.params.date_from)) + 1
                    : 0;
                // Несколько GET-запросов — суммируем totalVolume каждого ответа
                const values = responses.reduce((acc, r) => acc + gasTotalVolumeFromResult(r), 0);
                commit("KPI", { days, values });
                commit("LOADING", false);
                return;
            }

            const params = {
                organizationId: options.params.organizationId,
                date_from: options.params.date_from,
                date_to: options.params.date_to,
                page: options.params.page != null ? options.params.page : 1,
                pageSize: options.params.pageSize != null ? options.params.pageSize : 10
            };
            if (options.params.operationTypeId != null) {
                params.operationTypeId = options.params.operationTypeId;
            }
            if (options.params.isDailyData != null) {
                params.isDailyData = options.params.isDailyData;
            }
            const response = await axios.get(path, { params });
            const result = response && typeof response === "object" && !Array.isArray(response) && "data" in response ? response : { status: "Ok", data: response };
            const status = result && (result.status ?? result.Status);
            const payload = result && (result.data ?? result.Data);
            const isResultArray = Array.isArray(result);
            const isError = !isResultArray && result && status === "Error";
            if (isError) {
                const msg = (result.text ?? result.Text) || state.errorMessages.report;
                commit("ERROR_MESSAGE", msg);
                commit("ERROR", true);
                commit("GAS_DATA", []);
                commit("LOADING", false);
                return;
            }
            commit("ERROR", false);
            commit("ERROR_MESSAGE", "");
            let data = [];
            let totalVolumeValue = null;
            const payloadOrItems = payload && payload.data && (payload.items ?? payload.Items) === undefined
                ? payload.data
                : payload;
            if (Array.isArray(payloadOrItems)) {
                data = payloadOrItems;
            } else if (payloadOrItems && typeof payloadOrItems === "object") {
                const list = payloadOrItems.items ?? payloadOrItems.Items;
                const raw = Array.isArray(list) ? list : [];
                data = raw.map(row => ({
                    id: row.id ?? row.Id,
                    objectName: row.objectName ?? row.ObjectName,
                    organizationName: row.organizationName ?? row.OrganizationName,
                    dateTime: row.dateTime ?? row.DateTime,
                    regDateTime: row.regDateTime ?? row.RegDateTime,
                    temperature: row.temperature ?? row.Temperature,
                    volume: row.volume ?? row.Volume,
                    pressure: row.pressure ?? row.Pressure,
                    isDaily: row.isDaily ?? row.IsDaily
                }));
                const total = payloadOrItems.allCount ?? payloadOrItems.AllCount;
                if (total != null) commit("GAS_ALL_COUNT", Number(total));
                const totalVol = payloadOrItems.totalVolume ?? payloadOrItems.TotalVolume;
                if (totalVol != null) totalVolumeValue = Number(totalVol);
            } else if (isResultArray) {
                data = result;
            }
            commit("GAS_DATA", data);
            const chartData = gasReportToChartData(data, options.params.date_from, options.params.date_to);
            const chartTitle = options.params.date_from && options.params.date_to
                ? `График по газу за период ${format(new Date(options.params.date_from), "dd.MM.yyyy")} - ${format(new Date(options.params.date_to), "dd.MM.yyyy")}`
                : "";
            commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
            commit("CHART_OPTIONS_TITLE", chartTitle);
            commit("CHART_SERIES", { name: chartTitle, data: chartData });
            const days = options.params.date_from && options.params.date_to
                ? differenceInDays(Date.parse(options.params.date_to), Date.parse(options.params.date_from)) + 1
                : 0;
            const values = totalVolumeValue != null
                ? totalVolumeValue
                : (Array.isArray(data) ? data : []).reduce(
                    (acc, cur) => acc + (Number(cur.volume ?? cur.Volume) || 0),
                    0
                );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
            commit("GAS_DATA", []);
            commit("LOADING", false);
        }
    },
    fetchGasChartData: async ({ commit, state }, options) => {
        try {
            const path = "api/Chart/gas";
            const params = {
                date_from: options.params.date_from,
                date_to: options.params.date_to
            };
            if (options.params.organizationId != null && options.params.organizationId !== undefined && options.params.organizationId !== "") {
                params.organizationId = options.params.organizationId;
            }
            const response = await axios.get(path, { params });
            const result = response.data;
            if (result && result.isSuccess === false) {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
                return;
            }
            const rawData = result && Array.isArray(result.data) ? result.data : [];
            if (rawData.length > 0) {
                const chartData = convertGasChartData(rawData, options.params.date_from, options.params.date_to);
                const chartTitle = `График по газу за период ${format(new Date(options.params.date_from), "dd.MM.yyyy")} - ${format(new Date(options.params.date_to), "dd.MM.yyyy")}`;
                commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
                commit("CHART_OPTIONS_TITLE", chartTitle);
                commit("CHART_SERIES", { name: chartTitle, data: chartData });
            } else {
                commit("CHART_OPTIONS_TITLE", "");
                commit("CHART_SERIES", { name: "", data: [] });
            }
        } catch (e) {
            commit("CHART_OPTIONS_TITLE", "");
            commit("CHART_SERIES", { name: "", data: [] });
        }
    },

    resetGasTransportReport: ({ commit }) => {
        commit("GAS_TRANSPORT_DATA", []);
        commit("GAS_TRANSPORT_ALL_COUNT", 0);
        commit("KPI", { days: 0, values: 0 });
        commit("RANGE", { start: null, end: null });
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
        commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
    },
    fetchGasTransportOrganizations: async ({ commit, state }) => {
        try {
            const path = `${state.optionsGasTransport.path.base}/Organization`;
            const response = await axios.get(path);
            const raw = response.data;
            const list = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : []);
            const organizations = list.sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all);
            commit("GAS_TRANSPORT_ORGANIZATIONS", organizations);
            commit("ERROR", false);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchGasTransportData: async ({ commit, state }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsGasTransport.path.base;
            const params = {
                organizationId: options.params.organizationId,
                date_from: options.params.date_from,
                date_to: options.params.date_to,
                page: options.params.page != null ? options.params.page : 1,
                pageSize: options.params.pageSize != null ? options.params.pageSize : 10
            };
            if (options.params.operationTypeId != null) {
                params.operationTypeId = options.params.operationTypeId;
            }
            if (options.params.isDailyData != null) {
                params.isDailyData = options.params.isDailyData;
            }
            const response = await axios.get(path, { params });
            const result = response && typeof response === "object" && !Array.isArray(response) && "data" in response ? response : { status: "Ok", data: response };
            const status = result && (result.status ?? result.Status);
            const payload = result && (result.data ?? result.Data);
            const isResultArray = Array.isArray(result);
            const isError = !isResultArray && result && status === "Error";
            if (isError) {
                const msg = (result.text ?? result.Text) || state.errorMessages.report;
                commit("ERROR_MESSAGE", msg);
                commit("ERROR", true);
                commit("GAS_TRANSPORT_DATA", []);
                commit("LOADING", false);
                return;
            }
            commit("ERROR", false);
            commit("ERROR_MESSAGE", "");
            let data = [];
            let totalVolumeValue = null;
            const payloadOrItems = payload && payload.data && (payload.items ?? payload.Items) === undefined
                ? payload.data
                : payload;
            if (Array.isArray(payloadOrItems)) {
                data = payloadOrItems;
            } else if (payloadOrItems && typeof payloadOrItems === "object") {
                const list = payloadOrItems.items ?? payloadOrItems.Items;
                const raw = Array.isArray(list) ? list : [];
                data = raw.map(row => ({
                    id: row.id ?? row.Id,
                    objectName: row.objectName ?? row.ObjectName,
                    organizationName: row.organizationName ?? row.OrganizationName,
                    dateTime: row.dateTime ?? row.DateTime,
                    regDateTime: row.regDateTime ?? row.RegDateTime,
                    temperature: row.temperature ?? row.Temperature,
                    volume: row.volume ?? row.Volume,
                    pressure: row.pressure ?? row.Pressure,
                    isDaily: row.isDaily ?? row.IsDaily
                }));
                const total = payloadOrItems.allCount ?? payloadOrItems.AllCount;
                if (total != null) commit("GAS_TRANSPORT_ALL_COUNT", Number(total));
                const totalVol = payloadOrItems.totalVolume ?? payloadOrItems.TotalVolume;
                if (totalVol != null) totalVolumeValue = Number(totalVol);
            } else if (isResultArray) {
                data = result;
            }
            commit("GAS_TRANSPORT_DATA", data);
            const chartData = gasReportToChartData(data, options.params.date_from, options.params.date_to);
            const chartTitle = options.params.date_from && options.params.date_to
                ? `График по транспортировке газа за период ${format(new Date(options.params.date_from), "dd.MM.yyyy")} - ${format(new Date(options.params.date_to), "dd.MM.yyyy")}`
                : "";
            commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
            commit("CHART_OPTIONS_TITLE", chartTitle);
            commit("CHART_SERIES", { name: chartTitle, data: chartData });
            const days = options.params.date_from && options.params.date_to
                ? differenceInDays(Date.parse(options.params.date_to), Date.parse(options.params.date_from)) + 1
                : 0;
            const values = totalVolumeValue != null
                ? totalVolumeValue
                : (Array.isArray(data) ? data : []).reduce(
                    (acc, cur) => acc + (Number(cur.volume ?? cur.Volume) || 0),
                    0
                );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
            commit("GAS_TRANSPORT_DATA", []);
            commit("LOADING", false);
        }
    },
    resetGasRefineryReport: ({ commit }) => {
        commit("GAS_REFINERY_DATA", []);
        commit("GAS_REFINERY_ALL_COUNT", 0);
        commit("KPI", { days: 0, values: 0 });
        commit("RANGE", { start: null, end: null });
        commit("CHART_OPTIONS_TITLE", "");
        commit("CHART_SERIES", { name: "", data: [] });
        commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
    },
    fetchGasRefineryOrganizations: async ({ commit, state }) => {
        try {
            const path = `${state.optionsGasRefinery.path.base}/Organization`;
            const response = await axios.get(path);
            const raw = response.data;
            const list = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : []);
            const organizations = list.sort(sortByNameRuAscending);
            organizations.unshift(state.organization.all);
            commit("GAS_REFINERY_ORGANIZATIONS", organizations);
            commit("ERROR", false);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchGasRefineryData: async ({ commit, state }, options) => {
        try {
            commit("LOADING", true);
            const path = state.optionsGasRefinery.path.base;
            const params = {
                organizationId: options.params.organizationId,
                date_from: options.params.date_from,
                date_to: options.params.date_to,
                page: options.params.page != null ? options.params.page : 1,
                pageSize: options.params.pageSize != null ? options.params.pageSize : 10
            };
            if (options.params.operationTypeId != null) {
                params.operationTypeId = options.params.operationTypeId;
            }
            if (options.params.isDailyData != null) {
                params.isDailyData = options.params.isDailyData;
            }
            const response = await axios.get(path, { params });
            const result = response && typeof response === "object" && !Array.isArray(response) && "data" in response ? response : { status: "Ok", data: response };
            const status = result && (result.status ?? result.Status);
            const payload = result && (result.data ?? result.Data);
            const isResultArray = Array.isArray(result);
            const isError = !isResultArray && result && status === "Error";
            if (isError) {
                const msg = (result.text ?? result.Text) || state.errorMessages.report;
                commit("ERROR_MESSAGE", msg);
                commit("ERROR", true);
                commit("GAS_REFINERY_DATA", []);
                commit("LOADING", false);
                return;
            }
            commit("ERROR", false);
            commit("ERROR_MESSAGE", "");
            let data = [];
            let totalVolumeValue = null;
            const payloadOrItems = payload && payload.data && (payload.items ?? payload.Items) === undefined
                ? payload.data
                : payload;
            if (Array.isArray(payloadOrItems)) {
                data = payloadOrItems;
            } else if (payloadOrItems && typeof payloadOrItems === "object") {
                const list = payloadOrItems.items ?? payloadOrItems.Items;
                const raw = Array.isArray(list) ? list : [];
                data = raw.map(row => ({
                    id: row.id ?? row.Id,
                    objectName: row.objectName ?? row.ObjectName,
                    organizationName: row.organizationName ?? row.OrganizationName,
                    dateTime: row.dateTime ?? row.DateTime,
                    regDateTime: row.regDateTime ?? row.RegDateTime,
                    temperature: row.temperature ?? row.Temperature,
                    volume: row.volume ?? row.Volume,
                    pressure: row.pressure ?? row.Pressure,
                    isDaily: row.isDaily ?? row.IsDaily
                }));
                const total = payloadOrItems.allCount ?? payloadOrItems.AllCount;
                if (total != null) commit("GAS_REFINERY_ALL_COUNT", Number(total));
                const totalVol = payloadOrItems.totalVolume ?? payloadOrItems.TotalVolume;
                if (totalVol != null) totalVolumeValue = Number(totalVol);
            } else if (isResultArray) {
                data = result;
            }
            commit("GAS_REFINERY_DATA", data);
            const chartData = gasReportToChartData(data, options.params.date_from, options.params.date_to);
            const chartTitle = options.params.date_from && options.params.date_to
                ? `График по переработке газа за период ${format(new Date(options.params.date_from), "dd.MM.yyyy")} - ${format(new Date(options.params.date_to), "dd.MM.yyyy")}`
                : "";
            commit("CHART_YAXIS_TITLE", "Объем газа (м³)");
            commit("CHART_OPTIONS_TITLE", chartTitle);
            commit("CHART_SERIES", { name: chartTitle, data: chartData });
            const days = options.params.date_from && options.params.date_to
                ? differenceInDays(Date.parse(options.params.date_to), Date.parse(options.params.date_from)) + 1
                : 0;
            const values = totalVolumeValue != null
                ? totalVolumeValue
                : (Array.isArray(data) ? data : []).reduce(
                    (acc, cur) => acc + (Number(cur.volume ?? cur.Volume) || 0),
                    0
                );
            commit("KPI", { days, values });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
            commit("GAS_REFINERY_DATA", []);
            commit("LOADING", false);
        }
    },

    fetchAnomaliesData: async ({ commit }) => {
        try {
            const path = state.optionsAnomalies.path.base;
            const pathMin = state.optionsAnomalies.path.min;
            const pathMax = state.optionsAnomalies.path.max;
            commit("LOADING", true);
            const maxResponse = await axios.get(path + pathMax);
            commit("ANOMALIES_MAX_DATA", maxResponse.data);
            const minResponse = await axios.get(path + pathMin);
            commit("ANOMALIES_MIN_DATA", minResponse.data);
            commit("LOADING", false);
            commit("ANOMALIES_ITEM_SELECT", "max");
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    selectAnomaliesData: async ({ commit }, index) => {
        commit("ANOMALIES_ITEM_SELECT", index);
    },
    resetDataTransferAuditReport: ({ commit }) => {
        commit("TRANSFER_AUDIT_DATA", []);
        commit("RANGE", { start: null, end: null });
    },
    fetchDataTransferAuditOrganizations: async ({ commit, state }) => {
        try {
            const path =
                state.organization.path.base +
                state.optionsDataTransferAudit.facilityType +
                state.organization.path.sub;
            const response = await axios.get(path);
            commit(
                "TRANSFER_AUDIT_ORGANIZATIONS",
                response.data.sort(sortByNameRuAscending)
            );
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchDataTransferAuditData: async ({ commit }, options) => {
        try {
            // const path = state.optionsDataTransferAudit.path.base + options.path;
            const path = state.optionsDataTransferAudit.path.base;
            // const params = {
            //     org: options.params.organizationId,//TODO Унифицировать наименование переменных
            //     date_from: options.params.date_from,
            //     date_to: options.params.date_to
            // }
            commit("LOADING", true);
            // const response = await axios.get(path, { params });
            const response = await axios.get(path);
            const data = response.data;

            commit("TRANSFER_AUDIT_DATA", data);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    resetUsersActivityReport: ({ commit }) => {
        commit("LOADING", false);
        commit("REFINERY_DATA", []);
        commit("RANGE", { start: null, end: null });
    },
    fetchUsersActivityData: async ({ commit }, options) => {
        try {
            const path = state.optionsUsersActivity.path.base;
            const url = `${path}/${options.params.date_from}/${options.params.date_to}`;
            commit("LOADING", true);
            const response = await axios.get(url);
            const data = response.data;
            commit("USERS_ACTIVITY_DATA", data);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.report}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    resetRange: ({ commit }) => {
        commit("RANGE", { start: null, end: null });
    },
    currentTab: ({ commit }, data) => {
        commit("CURRENT_TAB", data);
    },
    fetchKPIOrganizations: async ({ commit }) => {
        try {
            const path = `api/Analytic/Plan/Organizations`;
            const response = await axios.get(path);
            const organizations = response.data
            commit("KPI_ORGANIZATIONS", organizations);
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchKPIPlans: async ({ commit }, options) => {
        const path = {
            'produce': `api/Analytic/Plan/Production`,
            'refinery': `api/Analytic/Plan/Refinery`,
            'export': `api/Analytic/Plan/Export`,
        };
        try {
            const response = await axios.get(`${path[options.type]}/?bin=${options.bin}&year=${options.year}`);
            const plan = response.data
            commit("SET_KPI_DATA", { type: options.type, plan });
            commit("LOADING", false);
        } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    },
    fetchCustomerKPIPlans: async ({ commit }) => {
        const bin = localStorage.getItem("bin");
        const year = new Date().getFullYear();
        try {
            const response = await axios.get(`api/Analytic/Plan/Customer/?bin=${bin}&year=${year}`);
            const plan = response.data;
            commit("SET_KPI_DATA", { type: "all", plan }); // Предполагается, что у вас есть "all" тип для всех табов
            commit("LOADING", false);
          } catch (e) {
            const message = `${state.errorMessages.organization}: ${e.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
          }
    },
    uploadPlans: async ({ commit }, options) => {
        const path = {
            'produce': `api/Analytic/Plan/Production/Upload`,
            'refinery': `api/Analytic/Plan/Refinery/Upload`,
            'export': `api/Analytic/Plan/Export/Upload`,
        };
        try {
            const response = await axios.post(`${path[options.type]}`, options.data);
            return(response); // Добавлено возвращение response
        } catch (error) {
            const message = `${state.errorMessages.organization}: ${error.message}`;
            commit("ERROR_MESSAGE", message);
            commit("ERROR", true);
        }
    }
};

const getters = {};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};

function convertDataChart(data) {
    // Данная система построения графиков (ApexChart)
    // по умолчанию воспринимает время в UTC.
    // Для отключения этой опции необходимо установить labels.datetimeUTC = false
    // При использовании утилит, типа format Date-FNS, эта опция просто должна быть удалена,
    // что равносильно состоянию true
    const reduceDates = [];
    const dates = data.map(item => ({
        x: format(new Date(item.date), 'yyyy-MM-dd'),
        y: item.mass
    }));
    dates.forEach((item) => {
        const currentDate = item.x;
        const currentIndex = reduceDates.findIndex(item => item.x === currentDate);
        currentIndex == -1
            ? reduceDates.push(item)
            : reduceDates[currentIndex].y += item.y
    });
    // console.log(reduceDates)
    return reduceDates;
}

function unwrapApiResponse(response) {
    if (response == null) return null;
    if (
        response.data != null &&
        typeof response.data === "object" &&
        (response.status != null || response.headers != null) &&
        ("Status" in response.data ||
            "status" in response.data ||
            "Data" in response.data ||
            "data" in response.data)
    ) {
        return response.data;
    }
    return response;
}

function getApiResultPayload(response) {
    const result = unwrapApiResponse(response);
    if (result == null) return [];
    if (Array.isArray(result)) return result;
    const status = result.status ?? result.Status;
    if (status === "Error") return [];
    const payload = result.data ?? result.Data;
    return Array.isArray(payload) ? payload : [];
}

function normalizeOilReportRow(row) {
    if (!row || typeof row !== "object") return row;
    return {
        ...row,
        organizationName: row.organizationName ?? row.OrganizationName ?? "",
        pipeline: row.pipeline ?? row.Pipeline ?? "",
        meteringDevice: row.meteringDevice ?? row.MeteringDevice ?? "",
        date: row.date ?? row.Date,
        mass: row.mass ?? row.Mass,
        temperature: row.temperature ?? row.Temperature,
        density: row.density ?? row.Density,
        volume: row.volume ?? row.Volume,
        dateTime: row.dateTime ?? row.DateTime ?? row.date ?? row.Date,
        productionFacilityName: row.productionFacilityName ?? row.ProductionFacilityName,
        pipelineName: row.pipelineName ?? row.PipelineName,
        massFlowBegin: row.massFlowBegin ?? row.MassFlowBegin,
        massFlowEnd: row.massFlowEnd ?? row.MassFlowEnd
    };
}

function normalizeOilReportItems(response) {
    return getApiResultPayload(response).map(row => normalizeOilReportRow(row));
}

function normalizeOrganizationsFromResponse(response) {
    return getApiResultPayload(response).map(org => ({
        ...org,
        id: org.id ?? org.Id,
        nameRu: org.nameRu ?? org.NameRu,
        bin: org.bin ?? org.Bin
    }));
}

function fillMissingDatesGasSeries(series, dateFrom, dateTo) {
    if (!Array.isArray(series) || !dateFrom || !dateTo) return series;
    const start = parseISO(dateFrom);
    const end = parseISO(dateTo);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return series;
    const dayCount = differenceInDays(end, start) + 1;
    if (dayCount <= 1) return series;

    const byDate = new Map();
    series.forEach(point => {
        const key = point && point.x ? String(point.x) : "";
        if (!key) return;
        byDate.set(key, Number(point.y) || 0);
    });

    const normalized = [];

    for (let i = 0; i < dayCount; i += 1) {
        const dateKey = format(addDays(start, i), "yyyy-MM-dd");
        normalized.push({
            x: dateKey,
            y: byDate.get(dateKey) ?? 0
        });
    }

    return normalized;
}

function convertGasChartData(data, dateFrom, dateTo) {
    if (!data || !Array.isArray(data)) return [];
    const sorted = [...data].sort((a, b) => new Date(a.dates) - new Date(b.dates));
    const points = sorted.map(item => ({
        x: format(new Date(item.dates), "yyyy-MM-dd"),
        y: item.series != null ? Number(item.series) : 0
    }));
    return fillMissingDatesGasSeries(points, dateFrom, dateTo);
}

function gasReportToChartData(reportItems, dateFrom, dateTo) {
    if (!reportItems || !Array.isArray(reportItems)) return [];
    const byDate = {};
    reportItems.forEach(item => {
        const key = item.dateTime ? format(new Date(item.dateTime), "yyyy-MM-dd") : "";
        if (!key) return;
        if (!byDate[key]) byDate[key] = 0;
        const v = item.volume != null ? Number(item.volume) : 0;
        byDate[key] += v;
    });
    const points = Object.entries(byDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([x, y]) => ({ x, y }));
    return fillMissingDatesGasSeries(points, dateFrom, dateTo);
}

function normalizeGasItemsFromResult(result) {
    const response = result && typeof result === "object" && !Array.isArray(result) && "data" in result
        ? result
        : { status: "Ok", data: result };
    const payload = response && (response.data ?? response.Data);
    const status = response && (response.status ?? response.Status);
    if (status === "Error") return [];

    const payloadOrItems = payload && payload.data && (payload.items ?? payload.Items) === undefined
        ? payload.data
        : payload;

    if (Array.isArray(payloadOrItems)) {
        return payloadOrItems.map(row => ({
            id: row.id ?? row.Id,
            objectName: row.objectName ?? row.ObjectName,
            organizationName: row.organizationName ?? row.OrganizationName,
            dateTime: row.dateTime ?? row.DateTime,
            regDateTime: row.regDateTime ?? row.RegDateTime,
            temperature: row.temperature ?? row.Temperature,
            volume: row.volume ?? row.Volume,
            pressure: row.pressure ?? row.Pressure,
            isDaily: row.isDaily ?? row.IsDaily
        }));
    }

    if (payloadOrItems && typeof payloadOrItems === "object") {
        const list = payloadOrItems.items ?? payloadOrItems.Items;
        const raw = Array.isArray(list) ? list : [];
        return raw.map(row => ({
            id: row.id ?? row.Id,
            objectName: row.objectName ?? row.ObjectName,
            organizationName: row.organizationName ?? row.OrganizationName,
            dateTime: row.dateTime ?? row.DateTime,
            regDateTime: row.regDateTime ?? row.RegDateTime,
            temperature: row.temperature ?? row.Temperature,
            volume: row.volume ?? row.Volume,
            pressure: row.pressure ?? row.Pressure,
            isDaily: row.isDaily ?? row.IsDaily
        }));
    }

    return [];
}

// Достаёт totalVolume из одного ответа GET. Если поля нет — суммирует объём по элементам этого ответа.
function gasTotalVolumeFromResult(result) {
    const response = result && typeof result === "object" && !Array.isArray(result) && "data" in result
        ? result
        : { status: "Ok", data: result };
    const payload = response && (response.data ?? response.Data);
    const status = response && (response.status ?? response.Status);
    if (status === "Error") return 0;

    const payloadOrItems = payload && payload.data && (payload.items ?? payload.Items) === undefined
        ? payload.data
        : payload;

    if (payloadOrItems && typeof payloadOrItems === "object" && !Array.isArray(payloadOrItems)) {
        const tv = payloadOrItems.totalVolume ?? payloadOrItems.TotalVolume;
        if (tv != null) return Number(tv) || 0;
    }

    return normalizeGasItemsFromResult(result)
        .reduce((acc, cur) => acc + (Number(cur.volume ?? cur.Volume) || 0), 0);
}